import React, { useEffect, useState } from 'react';
import { Box, Title, Text, useMantineColorScheme, Card, Stack, Group, Badge, Button, Container, Center, useMantineTheme, ActionIcon, SegmentedControl } from '@mantine/core';
import PageTransition from '../components/animations/PageTransition';
import { subscriptionPlanService, SubscriptionPlanDTO } from '../services/subscriptionPlanService';
import { userSubscriptionService, UserSubscriptionDTO } from '../services/userSubscriptionService';
import { mercadoPagoService, CreatePaymentPreferenceDTO } from '../services/mercadoPagoService';
import { authService } from '../services/authService';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { IconCheck, IconArrowLeft } from '@tabler/icons-react';

const MejorarPlanPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlanDTO[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscriptionDTO | null>(null);
  const [currentPlan, setCurrentPlan] = useState<any>(null); // Plan actual con información completa
  const [canUpgrade, setCanUpgrade] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planType, setPlanType] = useState('individual');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Intentar obtener la información completa de upgrade
        try {
          const upgradeInfo = await userSubscriptionService.getCurrentSubscription();
          
          if (upgradeInfo?.success) {
            setCurrentSubscription(upgradeInfo.currentSubscription);
            setCurrentPlan(upgradeInfo.currentPlan);
            setPlans(upgradeInfo.availablePlans || []);
            setCanUpgrade(upgradeInfo.canUpgrade ?? true);
            return; // Exit early if successful
          }
        } catch (upgradeError) {
          console.warn('Failed to get upgrade info:', upgradeError);
        }
        
        // Fallback: obtener solo los planes activos
        try {
          const plansData = await subscriptionPlanService.getAllActivePlans();
          setPlans(plansData || []);
          setCanUpgrade(true);
        } catch (fallbackError) {
          console.error('Failed to get plans:', fallbackError);
          throw fallbackError;
        }
        
      } catch (e: any) {
        console.error('Error loading plans:', e);
        setError('No se pudieron cargar los planes. Intenta recargar la página.');
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };
    
    load();
  }, []); // Empty dependency array to prevent re-runs

  const choosePlan = async (planId: number) => {
    try {
      setLoading(true);
      
      // Obtener información del usuario actual
      const currentUser = authService.getCurrentUserFromStorage();
      if (!currentUser) {
        notifications.show({
          color: 'red',
          title: 'Error de autenticación',
          message: 'Debes iniciar sesión para continuar'
        });
        navigate('/login');
        return;
      }

      // Obtener el plan seleccionado
      const selectedPlan = plans.find(p => p.id === planId);
      if (!selectedPlan) {
        notifications.show({
          color: 'red',
          title: 'Error',
          message: 'Plan no encontrado'
        });
        return;
      }

      // Si el plan es gratuito, cancelar suscripción actual con acceso residual
      if (selectedPlan.price === 0) {
        if (currentSubscription?.id) {
          try {
            await userSubscriptionService.cancelSubscriptionWithAccess(currentSubscription.id);
            notifications.show({
              color: 'blue',
              title: 'Plan cambiado',
              message: 'Tu suscripción se ha cancelado. Mantendrás acceso hasta el fin de tu período pagado.'
            });
            // Redirigir a billing para ver el cambio
            navigate('/settings/billing');
          } catch (error: any) {
            notifications.show({
              color: 'red',
              title: 'Error',
              message: error?.message || 'No se pudo cancelar la suscripción'
            });
          }
        } else {
          notifications.show({
            color: 'blue',
            title: 'Ya estás en plan gratuito',
            message: 'No tienes ninguna suscripción activa para cancelar'
          });
        }
        return;
      }

      // Para planes de pago, ir a checkout
      navigate(`/checkout/${planId}`);
      
    } catch (error: any) {
      console.error('Error procesando cambio de plan:', error);
      notifications.show({
        color: 'red',
        title: 'Error procesando cambio de plan',
        message: error?.message || 'No se pudo procesar el cambio. Inténtalo de nuevo.'
      });
    } finally {
      setLoading(false);
    }
  };

  const isCurrentPlan = (planId: number) => {
    return currentSubscription?.plan?.id === planId && currentSubscription?.status === 'ACTIVE';
  };

  const handleCurrentPlanClick = () => {
    // Redirigir al dashboard del estudiante
    navigate('/estudiante/dashboard');
  };

  return (
    <Box style={{
      minHeight: '100vh',
      padding: '16px 24px',
      overflow: 'hidden',
      overflowY: 'auto',
      background: colorScheme === 'dark'
        ? '#0a0a0a'
        : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
      position: 'relative',
    }}>
      <PageTransition type="medical" duration={600}>
        {/* Botón de navegación hacia atrás - posición absoluta */}
        <Box 
          style={{
            position: 'absolute',
            top: '2rem',
            left: '2rem',
            zIndex: 20,
          }}
        >
          <ActionIcon
            variant="default"
            size="lg"
            onClick={() => navigate('/settings/billing')}
            style={{
              backgroundColor: colorScheme === 'dark' 
                ? '#27272a' 
                : 'white',
              border: `1px solid ${colorScheme === 'dark' ? '#3f3f46' : '#e4e4e7'}`,
              borderRadius: '12px',
              color: colorScheme === 'dark' ? 'white' : '#18181b',
              transition: 'all 0.2s ease',
            }}
          >
            <IconArrowLeft size={20} stroke={1.5} />
          </ActionIcon>
        </Box>
        
        <Container size="xl" py="xl" style={{ position: 'relative', zIndex: 1 }}>
        
        <Center mb="xl">
          <Title
            order={1}
            size="2.5rem"
            fw={600}
            c={colorScheme === 'dark' ? 'white' : 'dark.6'}
            style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}
          >
            Elige tu plan de suscripción
          </Title>
        </Center>

        {loading && (
          <Text ta="center" c={colorScheme === 'dark' ? 'gray.4' : 'gray.6'}>Cargando planes...</Text>
        )}
        {error && (
          <Text c="red" ta="center">{error}</Text>
        )}

        <Group justify="center" align="stretch" gap="lg" style={{ maxWidth: '1600px', margin: '0 auto 3rem auto' }}>
          {plans.map((plan) => {
            const isCurrent = isCurrentPlan(plan.id);
            return (
              <Card
                key={plan.id}
                radius="xl"
                p="xl"
                bg={colorScheme === 'dark' 
                  ? 'rgba(20, 20, 20, 0.8)' 
                  : 'rgba(255, 255, 255, 0.9)'}
                withBorder
                shadow="sm"
                style={{
                  flex: '1 1 280px',
                  minWidth: 260,
                  maxWidth: 340,
                  display: 'flex',
                  flexDirection: 'column',
                  borderColor: colorScheme === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(221, 216, 209, 0.8)',
                  minHeight: '550px',
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                }}
              >
                <Stack gap="md" style={{ flex: 1 }}>
                  {/* Nombre del plan - arriba a la izquierda */}
                  <Box style={{ textAlign: 'left' }}>
                    <Text
                      fw={700}
                      size="1.25rem"
                      c={colorScheme === 'dark' ? 'white' : '#1e293b'}
                      style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}
                    >
                      {plan.name}
                    </Text>
                  </Box>

                  {/* Precio */}
                  <Box mb="xs" style={{ minHeight: '80px' }}>
                    <Text
                      size="2.5rem"
                      fw={700}
                      c={colorScheme === 'dark' ? 'white' : '#1e293b'}
                      lh={1}
                      style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}
                    >
                      ${Number(plan.price)}
                    </Text>
                    <Text
                      size="sm"
                      c={colorScheme === 'dark' ? '#71717a' : '#64748b'}
                      mt={4}
                    >
                      /{
                        plan.billingInterval === 'DAILY' ? 'día' :
                        plan.billingInterval === 'WEEKLY' ? 'semana' :
                        plan.billingInterval === 'MONTHLY' ? 'mes' :
                        plan.billingInterval === 'QUARTERLY' ? 'trimestre' :
                        plan.billingInterval === 'SEMIANNUAL' ? 'semestre' :
                        plan.billingInterval === 'YEARLY' ? 'año' : 'mes'
                      }
                    </Text>
                    {plan.description && (
                      <Text
                        size="sm"
                        c={colorScheme === 'dark' ? '#71717a' : '#64748b'}
                        mt={8}
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {plan.description}
                      </Text>
                    )}
                  </Box>

                  {/* Botón */}
                  <Button
                    size="md"
                    fullWidth
                    variant="filled"
                    color={isCurrent ? 'blue' : 'dark'}
                    onClick={() => isCurrent ? handleCurrentPlanClick() : choosePlan(plan.id)}
                    radius="lg"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      backgroundColor: colorScheme === 'dark' && !isCurrent ? '#e5e5e5' : undefined,
                      color: colorScheme === 'dark' && !isCurrent ? '#0a0a0a' : undefined,
                    }}
                  >
                    {isCurrent ? 'Continuar con este plan' : 'Cambiar a este plan'}
                  </Button>

                  {/* Divisor */}
                  <Box style={{
                    borderTop: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.1)'}`,
                    marginTop: '8px',
                    marginBottom: '8px',
                  }} />

                  {/* Features */}
                  <Stack gap="xs" style={{ flex: 1 }}>
                    {(plan.features || []).map((f, idx) => (
                      <Group key={idx} gap="sm" wrap="nowrap" align="flex-start">
                        <IconCheck
                          size={18}
                          color={colorScheme === 'dark' ? '#71717a' : '#64748b'}
                          style={{ flexShrink: 0, marginTop: 2 }}
                        />
                        <Text
                          size="sm"
                          c={colorScheme === 'dark' ? '#a1a1aa' : '#475569'}
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {f}
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </Group>
        
        </Container>
      </PageTransition>
    </Box>
  );
};

export default MejorarPlanPage;